import { Button, message, Modal, Spin } from "antd";
import { useState } from "react";
import { DownloadOutlined } from "@ant-design/icons";
import { apiPost } from "app/services/apiServices";
import { API_ENDPOINT_UPDATE_AUTO_SSR } from "../../../../credentials.constants";
import Uploader from "app/shared/Uploader";
import { FinkAnalytics } from "app/utils/GoogleAnalytics";

interface BulkUploadWidgetProps {
  onSuccess?: () => void;
}

export default function BulkUploadWidget({ onSuccess }: BulkUploadWidgetProps) {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const downloadSampleCSV = () => {
    const headers = [
      "customer",
      "forward_ssr_email",
      "client_email",
      "sap_client_code",
      "gst_number",
      "gst_name",
      "gst_state_name",
      "gst_state_code",
      "group_code",
    ];

    const sampleData = [
      ["Sample Customer", "sample@air.finkraft.ai", "client@example.com", "SAMPLE001", "29ABCDE1234F1Z5", "Sample GST Name", "Maharashtra","MH","GROUP001"],
    ];

    const csvContent = [
      headers.join(","),
      ...sampleData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "auto_ssr_sample.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = async (file: any) => {
    try {
      const allowedTypes = ["text/csv"];
      const fileType = file.type;

      // Check for valid file type
      if (!allowedTypes.includes(fileType)) {
        messageApi.error("Only CSV (.csv) files are allowed.");
        setLoading(false);
        return;
      }

      // Read the file content
      const text = await file.text();
      const lines = text.trim().split("\n");

      // Validate content
      if (lines.length <= 1 || lines.slice(1).every((line: string) => line.trim() === "")) {
        messageApi.error("The uploaded file is empty or contains no data.");
        setLoading(false);
        return;
      }

      // Extract headers
      const rawHeaderLine = lines[0];
      const headers = rawHeaderLine
        .split(",")
        .map((header: string) => header.trim().replace(/^"|"$/g, ""));

      // Check if the second row (index 1) is a sample row
      const isSampleRow = (rowValues: string[]): boolean => {
        // Sample row indicators: "Sample Customer", "sample@air.finkraft.ai", "SAMPLE001", etc.
        const sampleIndicators = [
          "Sample Customer",
          "sample@air.finkraft.ai",
          "client@example.com",
          "SAMPLE001",
          "29ABCDE1234F1Z5",
          "Sample GST Name",
          "Sample From Name",
          "GROUP001",
        ];
        
        // Check if any of the row values match sample indicators
        return rowValues.some((value) => 
          sampleIndicators.some((indicator) => 
            value && value.trim().toLowerCase().includes(indicator.toLowerCase())
          )
        );
      };

      // Determine starting index: skip sample row if it exists at index 1
      let startIndex = 1;
      if (lines.length > 1) {
        const secondRow = lines[1]
          .split(",")
          .map((cell: string) => cell.trim().replace(/^"|"$/g, ""));
        
        if (isSampleRow(secondRow)) {
          startIndex = 2; // Skip the sample row
        }
      }

      // Parse CSV rows starting from the determined index
      const rows = lines.slice(startIndex).map((line: string) => {
        const row = line
          .split(",")
          .map((cell: string) => cell.trim().replace(/^"|"$/g, ""));

        // Create object from headers and row values
        const rowObject: Record<string, any> = {};
        headers.forEach((header:any, index:any) => {
          rowObject[header] = row[index] || "";
        });
        return rowObject;
      });

      // Filter out empty rows
      const validRows = rows.filter((row:any) => {
        return Object.values(row).some((value) => {
          const strValue = typeof value === 'string' ? value : String(value || '');
          return strValue && strValue.trim() !== "";
        });
      });

      if (validRows.length === 0) {
        messageApi.error("No valid data found in the CSV file.");
        setLoading(false);
        return;
      }

      setLoading(true);

      // Process each row
      const results = {
        success: 0,
        failed: 0,
        errors: [] as string[],
      };

      for (const row of validRows) {
        try {
          // Clean up the row data - convert empty strings to proper format
          const cleanedRow: Record<string, any> = {};
          Object.keys(row).forEach((key) => {
            const value = row[key]?.trim() || "";
            cleanedRow[key] = value === "-" || value === "" ? "" : value;
          });

          const response = await apiPost(API_ENDPOINT_UPDATE_AUTO_SSR, cleanedRow);

          if (response.status) {
            results.success++;
          } else {
            results.failed++;
            results.errors.push(
              `Failed to upload row with customer: ${cleanedRow.customer || "N/A"}`
            );
          }
        } catch (error) {
          results.failed++;
          results.errors.push(
            `Error uploading row with customer: ${row.customer || "N/A"}`
          );
        }
      }

      setLoading(false);

      if (results.success > 0) {
        messageApi.success(
          `Successfully uploaded ${results.success} row(s).${results.failed > 0 ? ` ${results.failed} row(s) failed.` : ""}`
        );
        setShowModal(false);
        onSuccess && onSuccess();
      } else {
        messageApi.error(`Failed to upload all rows. Please check the data and try again.`);
      }

      if (results.errors.length > 0 && results.errors.length <= 5) {
        results.errors.forEach((error) => {
          messageApi.warning(error);
        });
      }
    } catch (error) {
      setLoading(false);
      messageApi.error("Error processing the CSV file. Please check the format and try again.");
    }
  };

  return (
    <>
      {contextHolder}
      <Button
        type="primary"
        onClick={() => {
          FinkAnalytics("user_opened_bulk_upload_modal", {
            event_category: "modal_open",
            event_label: "Bulk Upload Modal Opened",
          });
          setShowModal(true);
        }}
      >
        Bulk Upload
      </Button>

      <Modal
        footer={false}
        title="Bulk Upload Auto SSR"
        open={showModal}
        onCancel={() => {
          setShowModal(false);
        }}
      >
        <Spin spinning={isLoading}>
          <div>
            <div
              onClick={() => {
                FinkAnalytics("user_clicked_download_sample_csv", {
                  event_category: "button_click",
                  event_label: "Download Sample CSV Clicked",
                });
                downloadSampleCSV();
              }}
              style={{
                fontSize: "14px",
                border: "1px solid #0a8394",
                borderRadius: "5px",
                width: "180px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                marginBottom: "8px",
                backgroundColor: "white",
                color: "#0a8394",
                marginLeft: "auto",
                padding: "8px",
              }}
            >
              <DownloadOutlined style={{ marginRight: "4px", color: "#0a8394" }} />
              Download Sample CSV
            </div>
            <Uploader
              onSelect={(file: any) => {
                handleUpload(file);
              }}
            />
          </div>
        </Spin>
      </Modal>
    </>
  );
}

